"use client"

import React, { useEffect, useRef, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faEnvelope,
  faCalendarDays,
  faArrowRight,
  faCheck,
} from "@fortawesome/free-solid-svg-icons"
import {
  faLinkedinIn,
  faInstagram,
  faFacebookF,
} from "@fortawesome/free-brands-svg-icons"
import { config } from "@fortawesome/fontawesome-svg-core"
import "@fortawesome/fontawesome-svg-core/styles.css"

// Prevent FontAwesome from adding its CSS automatically since we import it above
config.autoAddCss = false

interface SitePublicSettings {
  siteName: string
  siteDescription: string
  siteUrl: string
  contactEmail: string
  facebookUrl: string
  twitterUrl: string
  linkedinUrl: string
  instagramUrl: string
}

const DEFAULT_SITE: SitePublicSettings = {
  siteName: "The Vantage Crest",
  siteDescription:
    "Connecting business leaders, investors, and institutions across high-growth international corridors.",
  siteUrl: "https://thevantagecrest.com",
  contactEmail: "hello@thevantagecrest.com",
  facebookUrl: "https://www.facebook.com/profile.php?id=61577972609044",
  twitterUrl: "",
  linkedinUrl: "https://www.linkedin.com/company/the-vantage-crest/",
  instagramUrl: "https://www.instagram.com/thevantagecrest/",
}

export function Footer() {
  const [email, setEmail] = useState("")
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [site, setSite] = useState<SitePublicSettings>(DEFAULT_SITE)

  // Pull site name, contact email and social links from admin Settings
  // (cached server-side in the public endpoint) with graceful fallback.
  useEffect(() => {
    fetch("/api/public/settings", { cache: "no-store" })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) setSite((prev) => ({ ...prev, ...data }))
      })
      .catch(() => {})
  }, [])

  const connectLinks = [
    { icon: faEnvelope, label: site.contactEmail || site.siteName, href: `mailto:${site.contactEmail}` },
    ...(site.linkedinUrl ? [{ icon: faLinkedinIn, label: "LinkedIn", href: site.linkedinUrl }] : []),
    ...(site.instagramUrl ? [{ icon: faInstagram, label: "Instagram", href: site.instagramUrl }] : []),
    ...(site.facebookUrl ? [{ icon: faFacebookF, label: "Facebook", href: site.facebookUrl }] : []),
    { icon: faCalendarDays, label: "Events Calendar", href: "/events" },
  ]

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setSubmitted(false)
    const isValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)
    if (!isValid) {
      setError("Please enter a valid email address.")
      setLoading(false)
      return
    }
    try {
      const res = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      })
      const data = await res.json()
      if (!res.ok || data?.ok === false) {
        setError(data?.message || "Something went wrong. Please try again.")
      } else if (data?.alreadyAdded) {
        setError(null)
        setSubmitted(true)
        setTimeout(() => setSubmitted(false), 3500)
      } else {
        setSubmitted(true)
        setEmail("")
        inputRef.current?.blur()
        setTimeout(() => setSubmitted(false), 3500)
      }
    } catch {
      setError("Network error. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <footer
      className="relative overflow-hidden bg-[var(--home-navy-deep,#001c3a)] text-[var(--home-cream,#f6f1e6)] border-t border-[color-mix(in_oklch,var(--home-gold,#c4a056)_24%,transparent)]"
      aria-label="Site Footer"
    >
      {/* Subtle ambient lighting */}
      <div
        className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-5xl h-72 opacity-30 blur-3xl z-0"
        style={{
          background: "radial-gradient(ellipse at top, color-mix(in oklch, var(--home-gold, #c4a056) 20%, transparent) 0%, transparent 70%)"
        }}
        aria-hidden
      />

      <div className="home-wrap relative z-10 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-10">
          {/* Col 1: Brand & Strategic Brief */}
          <div className="lg:col-span-4 flex flex-col items-start space-y-4">
            <Link href="/" className="flex items-center gap-3.5 group">
              <div className="relative w-9 h-9 rounded-sm overflow-hidden flex-shrink-0 bg-white/5 p-1 border border-[color-mix(in_oklch,var(--home-gold,#c4a056)_30%,transparent)]">
                <Image
                  src="/vantage-crest-icon.png"
                  alt="The Vantage Crest"
                  width={36}
                  height={36}
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="font-heading font-medium text-xl tracking-tight text-[var(--home-cream,#f6f1e6)]">
                {site.siteName}
              </span>
            </Link>

            <p className="text-sm text-[color-mix(in_oklch,var(--home-cream,#f6f1e6)_72%,var(--home-navy,#002d5c))] leading-relaxed max-w-sm pt-2">
              {site.siteDescription}
            </p>
          </div>

          {/* Col 2: Sitemap */}
          <div className="lg:col-span-2">
            <h3 className="font-heading text-sm font-medium tracking-[0.1em] uppercase text-[var(--home-gold,#c4a056)] mb-5">
              Sitemap
            </h3>
            <ul className="space-y-3 text-sm text-[color-mix(in_oklch,var(--home-cream,#f6f1e6)_75%,var(--home-navy,#002d5c))]">
              {[
                { label: "Home", href: "/" },
                { label: "Events", href: "/events" },
                { label: "About", href: "/about" },
                { label: "Articles", href: "/articles" },
                { label: "Contact", href: "/contact" },
              ].map((item) => (
                <li key={item.label}>
                  <Link
                    href={item.href}
                    className="hover:text-[var(--home-gold,#c4a056)] transition-colors duration-200 inline-block"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3: Connect */}
          <div className="lg:col-span-3">
            <h3 className="font-heading text-sm font-medium tracking-[0.1em] uppercase text-[var(--home-gold,#c4a056)] mb-5">
              Connect
            </h3>
            <ul className="space-y-3 text-sm text-[color-mix(in_oklch,var(--home-cream,#f6f1e6)_75%,var(--home-navy,#002d5c))]">
              {connectLinks.map(({ icon, label, href }) => (
                <li key={label}>
                  <a
                    href={href}
                    target={href.startsWith("http") ? "_blank" : undefined}
                    rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                    className="flex items-center gap-2.5 hover:text-[var(--home-gold,#c4a056)] transition-colors duration-200 group"
                  >
                    <FontAwesomeIcon
                      icon={icon}
                      className="w-4 h-4 text-[var(--home-gold,#c4a056)] opacity-80 group-hover:opacity-100 transition-opacity flex-shrink-0"
                    />
                    <span className="truncate">{label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4: Newsletter */}
          <div className="lg:col-span-3">
            <h3 className="font-heading text-sm font-medium tracking-[0.1em] uppercase text-[var(--home-gold,#c4a056)] mb-5">
              Stay Briefed
            </h3>
            <p className="text-xs text-[color-mix(in_oklch,var(--home-cream,#f6f1e6)_68%,var(--home-navy,#002d5c))] mb-4 leading-relaxed">
              Curated summit announcements, market intelligence, and executive introductions.
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-2.5" autoComplete="off">
              <input
                ref={inputRef}
                type="email"
                placeholder="Enter your email"
                className={`w-full px-3.5 py-2.5 text-sm bg-[color-mix(in_oklch,var(--home-navy,#002d5c)_85%,black)] border ${error
                    ? "border-red-400 focus:border-red-400"
                    : "border-[color-mix(in_oklch,var(--home-cream,#f6f1e6)_18%,transparent)] focus:border-[var(--home-gold,#c4a056)]"
                  } text-[var(--home-cream,#f6f1e6)] placeholder:text-[color-mix(in_oklch,var(--home-cream,#f6f1e6)_45%,var(--home-navy,#002d5c))] focus:outline-none transition-colors`}
                aria-label="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading || submitted}
                onFocus={() => setError(null)}
              />

              <button
                type="submit"
                className={`w-full py-2.5 px-4 bg-[var(--home-gold,#c4a056)] hover:bg-[#d8b56a] text-[var(--home-navy-deep,#001c3a)] font-medium text-xs uppercase tracking-[0.08em] transition-colors flex items-center justify-center gap-2 group ${loading ? "opacity-75 cursor-not-allowed" : ""
                  }`}
                disabled={loading || submitted}
              >
                {loading ? (
                  <span className="w-4 h-4 border-2 border-[var(--home-navy-deep,#001c3a)] border-t-transparent rounded-full animate-spin" />
                ) : submitted ? (
                  <span className="flex items-center gap-1.5 font-semibold">
                    <FontAwesomeIcon icon={faCheck} className="w-3.5 h-3.5" /> Subscribed
                  </span>
                ) : (
                  <>
                    <span>Subscribe</span>
                    <FontAwesomeIcon
                      icon={faArrowRight}
                      className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5"
                    />
                  </>
                )}
              </button>
            </form>

            <div className="min-h-[1.5em] mt-2">
              {error && <p className="text-xs text-red-400">{error}</p>}
              {submitted && (
                <p className="text-xs text-[var(--home-gold,#c4a056)]">
                  Thank you for subscribing to our executive briefings.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Bar: Copyright & Legal */}
        <div className="border-t border-[color-mix(in_oklch,var(--home-cream,#f6f1e6)_10%,transparent)] mt-16 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-[color-mix(in_oklch,var(--home-cream,#f6f1e6)_60%,var(--home-navy,#002d5c))]">
          <p>
            © {new Date().getFullYear()}{" "}
            <span className="text-[var(--home-cream,#f6f1e6)] font-medium">{site.siteName}</span>. All rights reserved.
          </p>

          <div className="flex flex-wrap gap-6 text-xs">
            <a
              href="#"
              className="hover:text-[var(--home-gold,#c4a056)] transition-colors duration-200"
              onClick={(e) => {
                e.preventDefault()
                if (typeof window !== "undefined" && (window as any).iubenda && typeof (window as any).iubenda.openPolicy === "function") {
                  (window as any).iubenda.openPolicy()
                } else {
                  window.open("https://www.iubenda.com/privacy-policy/16471004", "_blank", "noopener,noreferrer")
                }
              }}
            >
              Privacy Policy
            </a>
            <a
              href="#"
              className="hover:text-[var(--home-gold,#c4a056)] transition-colors duration-200"
              onClick={(e) => {
                e.preventDefault()
                if (typeof window !== "undefined" && (window as any).iubenda && typeof (window as any).iubenda.openCookiePolicy === "function") {
                  (window as any).iubenda.openCookiePolicy()
                } else {
                  window.open("https://www.iubenda.com/privacy-policy/16471004/cookie-policy", "_blank", "noopener,noreferrer")
                }
              }}
            >
              Cookie Policy
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}
